import React, { useState, useContext } from 'react';
import { Text, Link, IconName, Tooltip, Flex, Colors } from '@upsales/components';
import TimelineHistoryUsers from '../TimelineHistoryUsers';
import TimelineRow from '../TimelineRow';
import { OnChangeValue } from 'App/components/RelationSelect';
import T from 'Components/Helpers/translate';
import { EVENT_TIMELINE_SUB_TYPES } from 'App/babel/enum/timeline';
import RelationDropdownMenu from 'App/components/dropdowns/RelationDropdownMenu';
import MailResource from 'Resources/Mail';
import { TYPES } from 'Components/Helpers/SourceHelper';
import TimelineNoteBase from '../TimelineNoteBase';
import RelatedButtons from 'App/components/Buttons/RelatedButtons';
import logError from 'App/babel/helpers/logError';
import { openDrawer } from 'Services/Drawer';
import { PopupPortalAnchorsContext } from 'App/helpers/PopupPortalAnchors';
import { goToContactCard, getContactCardHref } from 'App/helpers/contactCardNavigation';
import Event from 'App/resources/Model/Event';
import MailOpenClickIndicator from 'Components/MailOpenClickIndicator';
import { TooltippedMailAISidebar } from 'Components/MailDrawers/MailAISidebar/MailAISidebar';
import { renderToString } from 'react-dom/server';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';

type Props = {
	event: Event;
	isHistoryLog: boolean;
	hideLine: boolean;
	hideIcon?: boolean;
};

const MAX_CONTACTS_TO_SHOW = 1;

const TimelineMail = ({ event, isHistoryLog, hideLine, hideIcon = false }: Props) => {
	const { contacts, date, entityId, mail, subType } = event || {};
	const isSent = subType?.toLowerCase() === EVENT_TIMELINE_SUB_TYPES.SEND;
	const fallbackContacts =
		!contacts?.length && mail
			? [{ id: 0, name: isSent ? mail.to : mail.fromName || mail.from, email: isSent ? mail.to : mail.from }]
			: null;
	const resolvedContacts = contacts?.length ? contacts : fallbackContacts;
	const contact = resolvedContacts?.[0];
	const contactsToShow = (resolvedContacts ?? []).slice(0, MAX_CONTACTS_TO_SHOW);
	const showContactsCount = (resolvedContacts ?? []).length - MAX_CONTACTS_TO_SHOW;

	const [relations, setRelations] = useState({
		opportunity: event.opportunity,
		appointment: event.appointment,
		activity: event.activity
	});
	const [editingMailRelations, setEditingMailRelations] = useState(false);

	const { scrollContainer, anchor } = useContext(PopupPortalAnchorsContext);

	if (!mail?.subject || !contact) {
		return null;
	}

	const onRelationChange = async (value: OnChangeValue) => {
		const { opportunityId, appointmentId, activityId } = value;
		try {
			const {
				data: { appointment, activity, opportunity }
			} = await MailResource.save({
				id: mail.id,
				opportunity: opportunityId ? { id: opportunityId } : null,
				appointment: appointmentId ? { id: appointmentId } : null,
				activity: activityId ? { id: activityId } : null
			});
			setRelations({ appointment, activity, opportunity });
		} catch (err) {
			logError(err, 'failed to save relations');
		}
	};
	const type = subType?.toLowerCase();
	let iconColor: Colors = '' as Colors;
	let title: JSX.Element | null = null;
	let icon: IconName = 'envelope';
	let Component: typeof TimelineHistoryUsers | typeof TimelineRow = TimelineHistoryUsers;
	let bounced = false;

	switch (type) {
		case EVENT_TIMELINE_SUB_TYPES.RECEIVE:
		case EVENT_TIMELINE_SUB_TYPES.SEND: {
			const isReceived = type === EVENT_TIMELINE_SUB_TYPES.RECEIVE;
			const isThreadWithEmails = mail.mailsInThreadCount && mail.mailsInThreadCount > 1;
			title = (
				<Text>
					{`${
						isReceived
							? T(isThreadWithEmails ? 'event.receivedMailInThread' : 'event.receivedMail').toLowerCase()
							: T(isThreadWithEmails ? 'default.mailSentInThread' : 'default.mailSent').toLowerCase()
					} `}

					<TooltippedMailAISidebar signals={mail.threadSignals}>
						<Link
							className="text-ellipsis"
							onClick={() => {
								openDrawer('SentMail', { mail: { id: entityId } });
							}}
						>
							<span className="text-ellipsis">{mail.subject}</span>
						</Link>
					</TooltippedMailAISidebar>

					{` ${isReceived ? T('default.from').toLowerCase() : T('default.to').toLowerCase()} `}
					{contactsToShow.map(contact =>
						contact.id ? (
							<Link key={contact.id} onClick={() => goToContactCard(contact.id)}>
								{contact.name}
							</Link>
						) : (
							<Text key="fallback" bold>
								{contact.name}
							</Text>
						)
					)}
					{showContactsCount > 0 ? (
						<Tooltip
							theme="white"
							interactive="true"
							style={{ cursor: 'pointer' }} // not adding a scss file for just this
							title={renderToString(
								<Flex gap="u1" direction="column" space="pts prs pbs pls">
									{resolvedContacts!.slice(MAX_CONTACTS_TO_SHOW).map(contact => (
										<Link key={contact.id} href={getContactCardHref(contact.id)}>
											{contact.name}
										</Link>
									))}
								</Flex>
							)}
						>{` ${T('default.and').toLowerCase()} ${showContactsCount} ${T('timeline.others')}`}</Tooltip>
					) : null}
				</Text>
			);
			break;
		}
		case EVENT_TIMELINE_SUB_TYPES.SOFT_BOUNCE:
		case EVENT_TIMELINE_SUB_TYPES.HARD_BOUNCE:
			bounced = true;
			Component = TimelineRow;
			icon = 'exclamation-triangle';
			iconColor = type === EVENT_TIMELINE_SUB_TYPES.SOFT_BOUNCE ? 'orange' : 'red';
			title = (
				<Text>
					{`${contact.name}'s ${T('event.emailBounced').toLowerCase()} (${T(
						type === EVENT_TIMELINE_SUB_TYPES.SOFT_BOUNCE
							? 'event.email_soft_bounce'
							: 'event.email_hard_bounce'
					).toLowerCase()}), `}
					<Link
						onClick={() => {
							openDrawer('SentMail', { mail: { id: entityId } });
						}}
					>{` ${mail.subject} `}</Link>
				</Text>
			);
			break;
		case EVENT_TIMELINE_SUB_TYPES.UNSUB:
			Component = TimelineRow;
			icon = 'unlink';
			iconColor = 'red';
			title = (
				<Text>
					{event.value
						? `${contact.name} ${T('default.unsubscribedFrom').toLowerCase()} ${event.value}, `
						: `${contact.name} ${T('mail.mailUnsubscribed').toLowerCase().replace('.', '')}, `}
					<Link
						onClick={() => {
							openDrawer('SentMail', { mail: { id: entityId } });
						}}
					>{` ${mail.subject} `}</Link>
				</Text>
			);
			break;
		default:
			break;
	}

	const subTitle = (
		<MailOpenClickIndicator mail={mail} date={date} bounced={bounced} showDate={true} showSentiment={true} />
	);
	const actions = hasSoftDeployAccess('TODO_LIST') ? (
		<div className="TimelineMail__actions">
			<RelatedButtons
				activity={relations.activity}
				appointment={relations.appointment}
				opportunity={relations.opportunity}
				smallIcons
				inEdit={editingMailRelations}
			/>
			<RelationDropdownMenu
				client={event.client}
				contact={contact}
				anchor={anchor}
				opportunityId={relations.opportunity?.id}
				appointmentId={relations.appointment?.id}
				activityId={relations.activity?.id}
				onRelationChange={onRelationChange}
				source={{ id: entityId, type: TYPES.MAIL }}
				onHideRelationSelect={() => setEditingMailRelations(false)}
				onShowRelationSelect={() => setEditingMailRelations(true)}
				tooltip={T('todo.showOptions')}
				scrollContainer={scrollContainer}
				iconColor="grey-8"
			/>
		</div>
	) : null;

	return (
		<TimelineNoteBase
			event={event}
			isHistoryLog={isHistoryLog}
			isLastElem={hideLine}
			content={
				<Component
					noIcon
					icon={hideIcon ? undefined : icon}
					iconColor={iconColor}
					event={event}
					title={title}
					subTitle={subTitle}
					actions={actions}
				/>
			}
			noTimelineRowMargin
		></TimelineNoteBase>
	);
};

export default TimelineMail;
